import { TradeInAemDetailFormItemType, TradeInAemNavStep, TradeInAemSearchType } from "../../../utils/constants";
import { TradeInCsl } from "../../TradeInCsl";
import { tradeInAemFindVehicleSetSearchResults } from "../../../redux/actions/TradeInAemFindVehicleActions";
import type {
    TradeInAemExtraVehicleDetailsPdfFiSpecificType,
    TradeInAemFiExtraVehicleDetailsPdfType,
    TradeInAemFiValueApiResponseType,
    TradeInAemVehicleTypeFi,
} from "./fi";
import type {
    TradeInAemDetailFormItem,
    TradeInAemEHubPayload,
    TradeInAemExtraVehicleDetailsPdfBaseType,
    TradeInAemVehicleNameType,
    TradeInAemVehicleType,
    TradeInAemVehicleValueType,
} from "../../../utils/types";
import { tradeInAemSetQuote, tradeInAenSetVehicleFetching } from "../../../redux/actions/TradeInAemVehicleValueActions";
import type { TradeInAemStateType } from "../../../redux/store";
import { formatNumber } from "../../../../common-deprecated/Globalize";
import { type TradeInAemLabelType, getTradeInAemLabel } from "../../../utils/labels";
import { enablePdf, getTradeInAemPdfUrl } from "../../../utils/utils";
import type { DealerFinderSettingsType } from "../../../../common-deprecated/components/AemDealerFinder";
import { mockTradeInAemFindVehicleResponse, mockTradeInAemVehicleValueResponse } from "../../../utils/mock-data/mocks";

// Using Fi request as mock data for now

export class TradeInCslTz extends TradeInCsl {
    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    enabledNavSteps = [
        TradeInAemNavStep.FindVehicle,
        TradeInAemNavStep.VehicleDetails,
        TradeInAemNavStep.VehicleValue,
        // tradeInAemNavSteps.modelSelect, // Model select is disabled as Tz has no new cars car-filter support
        TradeInAemNavStep.DealerFinder,
        TradeInAemNavStep.ContactForm,
        TradeInAemNavStep.Summary,
    ];

    enableVin = true;

    enableLicensePlate = true;

    enableResolve = true;

    enablePreviouslySearchedVehicles = true;

    resolveKeyMap: Record<string, TradeInAemLabelType> = {};

    dealerFinderSettings: DealerFinderSettingsType = {};

    // ----------------------------------------------------------------------
    // Selectors
    // ----------------------------------------------------------------------
    vehicleValueSelector(state: TradeInAemStateType): TradeInAemVehicleValueType | null {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemFiValueApiResponseType | null;
        return valuation?.quote?.value
            ? {
                  value: valuation.quote.value.min,
                  validityDate: valuation.quote.value.expires,
                  quoteId: valuation.quote.id,
              }
            : null;
    }

    vehiclePlateOrVinSelector(state: TradeInAemStateType): string {
        const { searchType } = state.tradeInAemFindVehicle;
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeFi;
        return searchType === TradeInAemSearchType.LicensePlate ? vehicle.plate : vehicle.vin;
    }

    vehicleInfoBlockSelector(state: TradeInAemStateType): { label: string; value: string }[] {
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeFi;
        if (!vehicle) return [];

        const { formItems } = state.tradeInAemVehicleDetails;
        const mileageValue = formatNumber(Number(formItems.mileage.value as string), state.commonSettings.culture.name);
        const mileageLabel = getTradeInAemLabel(state, "tradeInAemMileageFormat").replace("{value}", mileageValue);

        return [
            { label: getTradeInAemLabel(state, "tradeInAemVehicleDetailsLicensePlate"), value: vehicle.plate },
            { label: formItems.mileage.label, value: mileageLabel },
            { label: formItems.carPass.label, value: formItems.carPass.value as string },
            { label: formItems.vehicleCondition.label, value: formItems.vehicleCondition.value as string },
            { label: formItems.previousOwners.label, value: (formItems.previousOwners.value as string[]).join() },
        ];
    }

    vehicleQuoteExpirySelector(state: TradeInAemStateType): string {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemFiValueApiResponseType;
        if (!valuation?.quote?.value) return "";

        return getTradeInAemLabel(state, "tradeInAemVehicleValueValidity").replace(
            "{date}",
            valuation.quote.value.expires,
        );
    }

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------
    getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType {
        const typedVehicle = vehicle as TradeInAemVehicleTypeFi;
        return {
            name: `${typedVehicle.makeDescription} ${typedVehicle.modelDescription}`,
            description: typedVehicle.typeDescription,
        };
    }

    getVehicleKey(vehicle: TradeInAemVehicleType): string {
        const typedVehicle = vehicle as TradeInAemVehicleTypeFi;
        return `${typedVehicle.makeDescription}-${typedVehicle.modelDescription}-${typedVehicle.typeDescription}-${
            typedVehicle.typeETGCode
        }`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void> {
        const { dispatch } = this.getStore();
        return new Promise((resolve) => {
            const mockResponse = mockTradeInAemFindVehicleResponse;

            const searchResults: TradeInAemVehicleTypeFi[] = mockResponse.specification.vehicles.map((vehicle) => {
                return {
                    ...vehicle,
                    plate: mockResponse.plate,
                    vin: mockResponse.plateData.vin,
                    registrationDate: mockResponse.plateData.registrationDate,
                };
            });

            setTimeout(() => {
                dispatch(tradeInAemFindVehicleSetSearchResults(searchResults));
                resolve();
            }, 1000);
        });
    }

    fetchVehicleQuote(): Promise<void> {
        const { dispatch } = this.getStore();
        return new Promise((resolve) => {
            dispatch(tradeInAenSetVehicleFetching(true));

            setTimeout(() => {
                dispatch(tradeInAemSetQuote(mockTradeInAemVehicleValueResponse));
                resolve();
            }, 1000);
        });
    }

    getVehicleDetailFormItems(): { [formItemKey: string]: TradeInAemDetailFormItem } {
        return {
            mileage: {
                type: TradeInAemDetailFormItemType.Number,
                index: 0,
                label: "Mileage",
                required: false,
                value: "",
                description: "in km",
            },
            vehicleCondition: {
                type: TradeInAemDetailFormItemType.Radio,
                index: 2,
                label: "Condition of your vehicle",
                required: false,
                value: [],
                options: [
                    {
                        label: "Excellent",
                        description: "Your car is in great condition",
                        value: "excellent",
                    },
                    {
                        label: "Good",
                        description: "Your car has minor damage",
                        value: "good",
                    },
                    {
                        label: "Average",
                        description: "Your car has damage but is average for a vehicle of its age",
                        value: "average",
                    },
                    {
                        label: "Poor",
                        description: "Your car needs significant repair",
                        value: "poor",
                    },
                ],
            },
            previousOwners: {
                type: TradeInAemDetailFormItemType.Checkbox,
                index: 3,
                label: "Number of previous owners including yourself?",
                required: false,
                value: [],
                options: [
                    { label: "2", value: "2" },
                    { label: "3", value: "3" },
                    { label: "4+", value: "4+" },
                ],
            },
            carPass: {
                type: TradeInAemDetailFormItemType.Radio,
                index: 1,
                label: "Car pass available?",
                required: false,
                value: [],
                options: [
                    { label: "Yes", value: "yes" },
                    { label: "No", value: "no" },
                ],
                defaultValue: "yes",
            },
            registrationDate: {
                type: TradeInAemDetailFormItemType.Text,
                index: 4,
                label: "Registration date",
                description: "JJ-MM-AAAA",
                required: false,
                validationRule: "(0[1-9]|[1-2][0-9]|3[0-1])-(0[1-9]|1[0-2])-((19|20)[0-9][0-9])",
                validationError: "Please use JJ-MM-AAAA",
                value: "",
            },
        };
    }

    getEHubVehicleData(): TradeInAemEHubPayload["vehicle"] {
        const currentState = this.getStore().getState();
        const vehicleName = this.vehicleNameSelector(currentState);
        const { tradeInAemVehicle, tradeInAemVehicleDetails, commonSettings } = currentState;
        const { tradeInAemSettings } = currentState;
        // The vehicle on the PDF is fetched via the quoteId, which is propagated from the value api.
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemFiValueApiResponseType;

        const quoteId = this.vehicleValueSelector(currentState)?.quoteId || null;
        const pdfLink =
            enablePdf(commonSettings, tradeInAemSettings) && quoteId
                ? getTradeInAemPdfUrl(commonSettings, quoteId)
                : null;

        return {
            quoteId,
            licensePlate: vehicle.plate,
            name: `${vehicleName.name} - ${vehicleName.description || ""}`,
            manufacturer: vehicle.makeDescription,
            model: vehicle.modelDescription,
            bodyType: vehicle.bodyType,
            mileage: (tradeInAemVehicleDetails.formItems?.mileage?.value as string) || null,
            doors: vehicle.numberDoors.toString(),
            fuelType: vehicle.fuelType,
            engine: null,
            transmission: vehicle.gearType,
            co2: null,
            modelYear: vehicle.periodOfBuildDetails.poBFromYear.toString(),
            vin: vehicle.vin,
            pdfLink,
            registrationDate: vehicle.registrationDate,
        };
    }

    getExtraVehicleDetailsPdf = (): TradeInAemFiExtraVehicleDetailsPdfType => {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeFi;

        const baseObject: TradeInAemExtraVehicleDetailsPdfBaseType = {
            manufacturer: vehicle.makeDescription,
            model: vehicle.modelDescription,
            grade: null,
            generation: null,
            engine: null,
            powerHp: null,
            powerPs: vehicle.enginePS.toString(),
            powerKw: vehicle.engineKW.toString(),
            body: vehicle.bodyType,
            doors: vehicle.numberDoors.toString(),
            fuel: vehicle.fuelType,
            transmission: vehicle.gearType,
            gears: vehicle.numberGears.toString(),
            colour: null,
            regDate: vehicle.registrationDate,
            vin: vehicle.vin,
            seats: null,
            wheeldrive: null,
            cc: null,
            year: null,
        };

        // TAF specific
        const csObject: TradeInAemExtraVehicleDetailsPdfFiSpecificType = {
            engineSize: vehicle.engineSize,
            numberOfGears: vehicle.numberGears,
            priceBrutto: vehicle.priceBrutto,
            priceNetto: vehicle.priceNetto,
            driveType: vehicle.driveType,
            trimLine: vehicle.trimLine,
        };

        return { ...baseObject, ...csObject };
    };
}
